import {
  Component,
  OnInit,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter,
  HostListener,
} from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { MessageService } from 'primeng/api';
import { ReferenzaFirestore, RuoloProfilo } from '../../../core/models/firestore/referenza.model';
import { TagService } from '../../../services/tag.service';
import { RequisitiService } from '../../../services/requisiti.service';
import { ApiService } from '../../../services/api.service';
import { TagModel } from '../../../core/models/api/tag.model';
import { ClienteModel } from '../../../core/models/api/cliente.model';
import { CommessaModel } from '../../../core/models/api/commessa.model';
import { ReferenzaModel } from '../../../core/models/api/referenza.model';
import { TagRiferimentoModel } from '../../../core/models/api/tag-riferimento.model';
import { firstValueFrom } from 'rxjs';
import { CATEGORIE_SERVIZI } from '../../../core/constants/categorie-servizi.constants';
import { UserRoleService } from '../../../services/user-role.service';
import { RuoloReferenzaModel } from '../../../core/models/api/ruolo-referenza.model';
import { RuoliReferenzaService } from '../../../services/ruoli-referenza.service';
import { ProfiloModel } from '../../../core/models/api/profilo.model';
import { ProfiliService } from '../../../services/profili.service';
import type { LeafletLatLng } from '../../../shared/components/leaflet-map/leaflet-map.component';
import {
  GeocodingService,
  type GeocodeResult,
} from '../../../services/geocoding.service';

interface WizardStep {
  label: string;
  command?: (event?: any) => void;
}

interface RuoloAssignment {
  ruolo: RuoloReferenzaModel;
  persona: ProfiloModel | string | null;
  profiliSuggestions?: ProfiloModel[];
  firmatario: boolean;
}

@Component({
  selector: 'app-nuova-referenza-wizard',
  templateUrl: './nuova-referenza-wizard.component.html',
  styleUrl: './nuova-referenza-wizard.component.scss',
})
export class NuovaReferenzaWizardComponent implements OnInit, OnChanges {
  @Input() visible: boolean = false;
  @Input() referenzaToEdit: ReferenzaFirestore | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() referenzaCreated = new EventEmitter<void>();
  @Output() wizardClosed = new EventEmitter<void>();

  // Steps configuration
  currentStep: number = 0;
  steps: WizardStep[] = [
    { label: 'Informazioni Generali' },
    { label: 'Dettagli Progetto' },
    { label: 'Posizione' },
    { label: 'Tag' },
    { label: 'Ruoli e soggetti' },
    { label: 'Riepilogo' },
  ];

  /**
   * Step nascosti in modifica (Tag e Ruoli temporaneamente disabilitati)
   */
  private getSkippedStepIndexes(): number[] {
    return this.referenzaToEdit ? [3, 4] : [];
  }

  /**
   * Steps visibili: in modifica esclude Tag e Ruoli
   */
  get visibleSteps(): WizardStep[] {
    const skipped = this.getSkippedStepIndexes();
    if (skipped.length > 0) {
      return this.steps.filter((_, index) => !skipped.includes(index));
    }
    return this.steps;
  }

  /**
   * Mappa l'indice dello step corrente allo step reale
   */
  getRealStepIndex(displayIndex: number): number {
    const skipped = this.getSkippedStepIndexes();
    if (skipped.length === 0) {
      return displayIndex;
    }

    let displayCount = 0;
    for (let real = 0; real < this.steps.length; real++) {
      if (skipped.includes(real)) continue;
      if (displayCount === displayIndex) return real;
      displayCount++;
    }
    return displayIndex;
  }

  /**
   * Mappa lo step reale all'indice di visualizzazione
   */
  getDisplayStepIndex(realIndex: number): number {
    const skipped = this.getSkippedStepIndexes();
    if (skipped.length === 0) {
      return realIndex;
    }

    let displayIndex = 0;
    for (let real = 0; real < realIndex; real++) {
      if (skipped.includes(real)) continue;
      displayIndex++;
    }
    return displayIndex;
  }

  private getNextRealStep(currentRealStep: number): number | null {
    for (let i = currentRealStep + 1; i < this.steps.length; i++) {
      if (!this.getSkippedStepIndexes().includes(i)) return i;
    }
    return null;
  }

  private getPrevRealStep(currentRealStep: number): number | null {
    for (let i = currentRealStep - 1; i >= 0; i--) {
      if (!this.getSkippedStepIndexes().includes(i)) return i;
    }
    return null;
  }

  // Form data (usiamo Date per dataFine perché p-calendar restituisce Date)
  referenzaData: Partial<
    Omit<ReferenzaFirestore, 'dataFine'> & { dataFine: Date | null }
  > = {
    codiceCommerciale: null,
    codiceCertificato: null, // Manteniamo per retrocompatibilità, ma useremo codiciCertificati
    codiciCertificati: [],
    projectManager: null,
    cliente: null,
    titoloBreve: '',
    titoloEsteso: null,
    tempistiche: null,
    dataFine: null,
    rtp: false,
    capogruppo: false,
    quotaCooprogetti: null,
    inAq: false,
    bim: false,
    valoreDimensione: null,
    importoLavori: null,
    importoServizio: null,
    categorieServizio: [],
    tag: [],
    categorieTag: [],
    serviziAccessoriPresenti: false,
    urlServiziAccessori: null,
  };

  // Step Posizione (opzionale) - Nominatim (OpenStreetMap)
  geoAddressInput = '';
  geoCityInput = '';
  geoSearching = false;
  lastGeocodeConfidence: GeocodeResult['confidence'] | null = null;

  readonly geoDefaultCenter: LeafletLatLng = { lat: 41.9, lng: 12.5 };
  geoMapCenter: LeafletLatLng = this.geoDefaultCenter;
  geoMapZoom = 5;
  geoSelectedPosition: LeafletLatLng | null = null;
  geoMapHeightPx = 420;

  get posizioneLatitude(): number | null {
    return this.geoSelectedPosition ? this.geoSelectedPosition.lat : null;
  }
  get posizioneLongitudine(): number | null {
    return this.geoSelectedPosition ? this.geoSelectedPosition.lng : null;
  }

  onGeoMapSelectedPositionChange(pos: LeafletLatLng | null): void {
    this.geoSelectedPosition = pos;
    if (!pos) {
      this.geoMapCenter = this.geoDefaultCenter;
      this.geoMapZoom = 5;
      return;
    }
    this.geoMapCenter = pos;
    this.geoMapZoom = 16;
  }

  clearGeoPosizione(): void {
    this.geoSelectedPosition = null;
    this.geoMapCenter = this.geoDefaultCenter;
    this.geoMapZoom = 5;
  }

  // Tag management
  tagsByCategory: Record<string, TagModel[]> = {};
  allTags: TagModel[] = [];
  selectedTagsByCategory: Record<string, string[]> = {};
  catSuggestions: Record<string, string[]> = {};
  // Nuova categorizzazione tag: 5 categorie principali
  private readonly tagCategoryOrder: string[] = [
    'FASI',
    'TEMATICHE',
    'TIPOLOGIA',
    'PRESTAZIONI',
    'TIPO_STRUTTURE',
  ];
  private readonly tagCategoryIcons: Record<string, string> = {
    FASI: 'timeline',
    TEMATICHE: 'schema',
    TIPOLOGIA: 'category',
    PRESTAZIONI: 'speed',
    TIPO_STRUTTURE: 'apartment',
  };
  groupedCategories: Array<{
    groupName: string;
    groupLabel: string;
    groupIcon: string;
    categories: Array<{ key: string; value: TagModel[] }>;
  }> = [];

  // Dialog creazione tag
  showCreateTagDialog = false;
  createTagInitialCategory: string | null = null;

  // Dialog creazione cliente e commessa
  showCreateClienteDialog = false;
  showCreateCommessaDialog = false;

  // Categorie servizio
  categoriaServizioSuggestions: string[] = [];
  categoriaServizioInput: string = '';
  categorieServizioDisponibili: string[] = CATEGORIE_SERVIZI;

  // Codici certificato
  codiceCertificatoInput: string = '';

  // Esiti del caricamento dati di supporto del wizard (clienti, commesse, ruoli, profili).
  // Serve a distinguere "elenco vuoto" da "caricamento fallito" e a offrire un retry.
  datiWizardNonCaricati: string[] = [];
  ricaricaDatiWizardInCorso = false;
  private caricamentoDatiPromise: Promise<void> | null = null;

  // Clienti e Commesse per autocompletamento
  clienti: ClienteModel[] = [];
  clientiSuggestions: ClienteModel[] = [];
  selectedCliente: ClienteModel | null = null;
  commesse: CommessaModel[] = [];
  commesseSuggestions: CommessaModel[] = [];
  selectedCommessa: CommessaModel | null = null;
  codiceCommessaInput: string = '';

  // Profili (persone) per assegnazione ruoli
  profili: ProfiloModel[] = [];
  showCreateProfiloDialog = false;
  savingNewProfilo = false;
  newProfiloNome = '';
  newProfiloCognome = '';
  targetAssignmentForNewProfilo: RuoloAssignment | null = null;

  // Ruoli e soggetti
  ruoliDisponibili: RuoloReferenzaModel[] = [];
  ruoliFiltrati: RuoloReferenzaModel[] = [];
  ruoliAssignments: RuoloAssignment[] = [];
  ruoliSearchText = '';
  /** Snapshot dei ruoli originali in modifica, usato per il diff al salvataggio */
  private originalRuoliProfili: RuoloProfilo[] = [];

  // Loading states
  loading = false;
  saving = false;

  constructor(
    private logger: NGXLogger,
    private messageService: MessageService,
    private tagService: TagService,
    private requisitiService: RequisitiService,
    private apiService: ApiService,
    private userRoleService: UserRoleService,
    private ruoliReferenzaService: RuoliReferenzaService,
    private profiliService: ProfiliService,
    private geocodingService: GeocodingService,
  ) {}

  ngOnInit(): void {
    this.computeGeoMapHeight();
  }

  /**
   * Intercetta i cambiamenti di visible per caricare i dati quando il wizard viene aperto
   */
  ngOnChanges(changes: SimpleChanges): void {
    const wizardJustOpened =
      changes['visible'] && changes['visible'].currentValue === true;

    if (wizardJustOpened) {
      // Il wizard è stato aperto: carica tutto da zero
      this.currentStep = 0;
      this.loadTags();
      this.onWizardShow().then(() => {
        if (this.referenzaToEdit) {
          this.loadReferenzaData(this.referenzaToEdit);
        } else {
          this.resetForm();
        }
      });
      // Non processare il secondo blocco: referenzaToEdit è già gestita qui sopra
      return;
    }

    // Se cambia la referenza da modificare mentre il wizard è già aperto
    if (changes['referenzaToEdit'] && this.visible) {
      this.currentStep = 0;
      this.onWizardShow().then(() => {
        if (this.referenzaToEdit) {
          this.loadReferenzaData(this.referenzaToEdit);
        } else {
          this.resetForm();
        }
      });
    }
  }

  /**
   * Metodo chiamato quando il wizard viene aperto
   * Restituisce una Promise che si risolve quando clienti e commesse sono caricati
   */
  async onWizardShow(): Promise<void> {
    this.logger.debug(
      '[WIZARD] Wizard aperto, carico clienti, commesse, ruoli e profili',
    );

    // Reset sicuro dei tag selezionati ad ogni apertura del wizard
    // in modo che l'elenco parta sempre da zero
    this.selectedTagsByCategory = {};
    this.referenzaData.tag = [];
    this.referenzaData.categorieTag = [];

    await this.caricaDatiDiSupporto();
  }

  /**
   * Carica gli elenchi di supporto del wizard (clienti, commesse, ruoli, profili).
   * Ogni elenco è indipendente: quelli recuperati vengono valorizzati anche se
   * altri falliscono, e i falliti restano tracciati in `datiWizardNonCaricati`.
   */
  private caricaDatiDiSupporto(): Promise<void> {
    // onShow (template) e ngOnChanges invocano entrambi l'apertura del wizard:
    // senza questa deduplica il caricamento partirebbe due volte, con doppie
    // chiamate al backend e avvisi duplicati. I chiamati in attesa ricevono la
    // stessa promise, così `.then()` resta sincronizzato con il caricamento reale.
    if (this.caricamentoDatiPromise) {
      return this.caricamentoDatiPromise;
    }

    this.caricamentoDatiPromise = this.eseguiCaricamentoDatiDiSupporto().finally(
      () => {
        this.caricamentoDatiPromise = null;
      },
    );

    return this.caricamentoDatiPromise;
  }

  private async eseguiCaricamentoDatiDiSupporto(): Promise<void> {
    // allSettled e non all: il fallimento di una sola chiamata non deve impedire
    // di valorizzare gli elenchi che sono stati recuperati correttamente.
    const [clientiRes, commesseRes, ruoliRes, profiliRes] =
      await Promise.allSettled([
        firstValueFrom(this.requisitiService.getClienti()),
        firstValueFrom(this.requisitiService.getCommesse()),
        firstValueFrom(this.ruoliReferenzaService.getRuoli()),
        firstValueFrom(this.profiliService.getProfili()),
      ]);

    const nonCaricati: string[] = [];

    if (clientiRes.status === 'fulfilled') {
      this.clienti = clientiRes.value ?? [];
      this.logger.debug('[WIZARD] Clienti caricati:', this.clienti.length);
    } else {
      nonCaricati.push('clienti');
      this.logger.error(
        '[WIZARD] Errore nel caricamento dei clienti:',
        clientiRes.reason,
      );
    }

    if (commesseRes.status === 'fulfilled') {
      this.commesse = commesseRes.value ?? [];
      this.logger.debug('[WIZARD] Commesse caricate:', this.commesse.length);
    } else {
      nonCaricati.push('commesse');
      this.logger.error(
        '[WIZARD] Errore nel caricamento delle commesse:',
        commesseRes.reason,
      );
    }

    if (ruoliRes.status === 'fulfilled') {
      this.ruoliDisponibili = ruoliRes.value ?? [];
      this.ruoliFiltrati = [...this.ruoliDisponibili];
      this.logger.debug(
        '[WIZARD] Ruoli referenza caricati:',
        this.ruoliDisponibili.length,
      );
    } else {
      nonCaricati.push('ruoli');
      this.logger.error(
        '[WIZARD] Errore nel caricamento dei ruoli:',
        ruoliRes.reason,
      );
    }

    if (profiliRes.status === 'fulfilled') {
      this.profili = (profiliRes.value ?? [])
        .map((p) => ({
          ...p,
          displayName: `${p.nome} ${p.cognome}`.trim(),
        }))
        .sort((a, b) => {
          const byCognome = a.cognome.localeCompare(b.cognome, 'it', {
            sensitivity: 'base',
          });
          if (byCognome !== 0) return byCognome;
          return a.nome.localeCompare(b.nome, 'it', { sensitivity: 'base' });
        });
      this.logger.debug('[WIZARD] Profili caricati:', this.profili.length);
    } else {
      nonCaricati.push('profili');
      this.logger.error(
        '[WIZARD] Errore nel caricamento dei profili:',
        profiliRes.reason,
      );
    }

    // Nessun toast: l'avviso vive nel banner in cima al wizard, che resta
    // visibile finché il problema persiste e porta con sé l'azione di ricarica.
    this.datiWizardNonCaricati = nonCaricati;
  }

  /**
   * Riprova il caricamento dei dati di supporto del wizard dopo un errore,
   * senza costringere l'utente a chiudere e riaprire la finestra.
   */
  async retryCaricamentoDatiWizard(): Promise<void> {
    if (this.ricaricaDatiWizardInCorso) {
      return;
    }

    this.ricaricaDatiWizardInCorso = true;
    try {
      // L'esito è già visibile: se il caricamento riesce il banner sparisce e
      // gli elenchi si popolano. Nessun toast di conferma da chiudere a mano.
      await this.caricaDatiDiSupporto();
    } finally {
      this.ricaricaDatiWizardInCorso = false;
    }
  }

  /**
   * Filtra la lista dei ruoli disponibili in base al testo di ricerca
   */
  filterRuoliList(): void {
    const term = this.ruoliSearchText.toLowerCase().trim();
    if (!term) {
      this.ruoliFiltrati = [...this.ruoliDisponibili];
    } else {
      this.ruoliFiltrati = this.ruoliDisponibili.filter(
        (ruolo) =>
          ruolo.descrizione.toLowerCase().includes(term) ||
          ruolo.codice.toLowerCase().includes(term),
      );
    }
  }

  isRuoloSelected(ruolo: RuoloReferenzaModel): boolean {
    return this.ruoliAssignments.some((a) => a.ruolo.id === ruolo.id);
  }

  toggleRuoloSelection(ruolo: RuoloReferenzaModel): void {
    if (this.isRuoloSelected(ruolo)) {
      this.ruoliAssignments = this.ruoliAssignments.filter(
        (a) => a.ruolo.id !== ruolo.id,
      );
    } else {
      this.ruoliAssignments = [
        ...this.ruoliAssignments,
        {
          ruolo,
          persona: null,
          firmatario: false,
        },
      ];
    }
  }

  selectAllRuoli(): void {
    this.ruoliAssignments = this.ruoliDisponibili.map((ruolo) => {
      const existing = this.ruoliAssignments.find(
        (a) => a.ruolo.id === ruolo.id,
      );
      return (
        existing || {
          ruolo,
          persona: null,
          firmatario: false,
        }
      );
    });
  }

  clearAllRuoli(): void {
    this.ruoliAssignments = [];
  }

  removeRuoloAssignment(assignment: RuoloAssignment): void {
    this.ruoliAssignments = this.ruoliAssignments.filter(
      (a) => a.ruolo.id !== assignment.ruolo.id,
    );
  }

  /**
   * Duplica una riga di ruolo per assegnare un secondo profilo allo stesso ruolo
   */
  duplicateRuoloAssignment(assignment: RuoloAssignment): void {
    this.ruoliAssignments = [
      ...this.ruoliAssignments,
      {
        ruolo: assignment.ruolo,
        persona: null,
        firmatario: false,
      },
    ];
  }

  /**
   * Ritorna il nome completo di un profilo
   */
  getProfiloFullName(
    profilo: ProfiloModel | string | null | undefined,
  ): string {
    if (!profilo) return '';
    if (typeof profilo === 'string') return profilo;
    return profilo.displayName || `${profilo.nome} ${profilo.cognome}`.trim();
  }

  private isProfiloModel(value: unknown): value is ProfiloModel {
    return (
      !!value &&
      typeof value === 'object' &&
      'id' in value &&
      typeof (value as ProfiloModel).id === 'number'
    );
  }

  private parseManualProfilo(
    value: ProfiloModel | string | null | undefined,
  ): { nome: string; cognome: string } | null {
    if (!value) return null;
    if (this.isProfiloModel(value)) {
      return null;
    }

    const fullName = value.trim().replace(/\s+/g, ' ');
    if (!fullName) return null;

    const parts = fullName.split(' ');
    if (parts.length < 2) return null;

    const nome = parts[0].trim();
    const cognome = parts.slice(1).join(' ').trim();
    if (!nome || !cognome) return null;

    return { nome, cognome };
  }

  private assignmentMatchesRuoloProfilo(
    assignment: RuoloAssignment,
    ruoloProfilo: RuoloProfilo,
  ): boolean {
    if (assignment.ruolo.codice !== ruoloProfilo.ruoloCodice) {
      return false;
    }

    if (this.isProfiloModel(assignment.persona)) {
      return assignment.persona.id === ruoloProfilo.profiloId;
    }

    if (typeof assignment.persona === 'string') {
      return (
        assignment.persona.trim().toLowerCase() ===
        (ruoloProfilo.nomeCompleto ?? '').trim().toLowerCase()
      );
    }

    return false;
  }

  private hasRuoliAssignmentsChanged(): boolean {
    const currentWithPersona = this.ruoliAssignments.filter((a) => a.persona);
    if (currentWithPersona.length !== this.originalRuoliProfili.length) {
      return true;
    }

    return currentWithPersona.some(
      (assignment) =>
        !this.originalRuoliProfili.some((rp) =>
          this.assignmentMatchesRuoloProfilo(assignment, rp),
        ),
    );
  }

  private async syncRuoliOnEdit(referenzaId: number): Promise<void> {
    if (!this.hasRuoliAssignmentsChanged()) {
      this.logger.debug(
        '[WIZARD] Nessuna modifica ai ruoli, sincronizzazione saltata',
      );
      return;
    }

    const removals = this.originalRuoliProfili.filter(
      (rp) =>
        !this.ruoliAssignments.some((a) =>
          this.assignmentMatchesRuoloProfilo(a, rp),
        ),
    );

    const additions = this.ruoliAssignments.filter((assignment) => {
      if (!assignment.persona) return false;
      return !this.originalRuoliProfili.some((rp) =>
        this.assignmentMatchesRuoloProfilo(assignment, rp),
      );
    });

    await Promise.all([
      ...removals.map((rp) => {
        const ruolo = this.ruoliDisponibili.find(
          (r) => r.codice === rp.ruoloCodice,
        );
        if (!ruolo || !rp.profiloId) {
          return Promise.resolve();
        }
        return firstValueFrom(
          this.apiService.removeProfiloFromRuolo(
            referenzaId,
            ruolo.id,
            rp.profiloId,
          ),
        );
      }),
      ...additions.map((assignment) => {
        if (this.isProfiloModel(assignment.persona)) {
          return firstValueFrom(
            this.apiService.assignProfiloToRuolo(
              referenzaId,
              assignment.ruolo.id,
              assignment.persona.id,
              { firmatario: assignment.firmatario },
            ),
          );
        }

        const manual = this.parseManualProfilo(assignment.persona);
        if (!manual) {
          return Promise.resolve();
        }

        return firstValueFrom(
          this.apiService.assignManualProfiloToRuolo(
            referenzaId,
            assignment.ruolo.id,
            {
              nome: manual.nome,
              cognome: manual.cognome,
              firmatario: assignment.firmatario,
            },
          ),
        );
      }),
    ]);

    this.logger.info(
      '[WIZARD] Sincronizzazione ruoli completata: +',
      additions.length,
      '/ -',
      removals.length,
    );
  }

  /**
   * Autocomplete per i profili nella tabella ruoli
   */
  onProfiloComplete(
    event: { query: string },
    assignment: RuoloAssignment,
  ): void {
    const term = (event.query || '').toLowerCase().trim();
    // Profili già usati per questo ruolo in altre righe
    const usedProfiloIds = new Set(
      this.ruoliAssignments
        .filter(
          (a) =>
            a.ruolo.id === assignment.ruolo.id && a !== assignment && a.persona,
        )
        .map((a) => (this.isProfiloModel(a.persona) ? a.persona.id : null))
        .filter((id): id is number => id !== null),
    );

    if (!term) {
      assignment.profiliSuggestions = this.profili.filter(
        (p) => !usedProfiloIds.has(p.id),
      );
    } else {
      assignment.profiliSuggestions = this.profili.filter(
        (p) =>
          !usedProfiloIds.has(p.id) &&
          `${p.nome} ${p.cognome}`.toLowerCase().includes(term),
      );
    }
  }

  openCreateProfiloDialog(assignment: RuoloAssignment): void {
    this.targetAssignmentForNewProfilo = assignment;
    this.newProfiloNome = '';
    this.newProfiloCognome = '';
    this.savingNewProfilo = false;
    this.showCreateProfiloDialog = true;
  }

  closeCreateProfiloDialog(): void {
    this.showCreateProfiloDialog = false;
    this.savingNewProfilo = false;
    this.newProfiloNome = '';
    this.newProfiloCognome = '';
    this.targetAssignmentForNewProfilo = null;
  }

  isNewProfiloFormValid(): boolean {
    return (
      this.newProfiloNome.trim().length > 0 &&
      this.newProfiloCognome.trim().length > 0
    );
  }

  private isProfiloDuplicate(nome: string, cognome: string): boolean {
    const normalize = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase();
    const nomeNorm = normalize(nome);
    const cognomeNorm = normalize(cognome);
    return this.profili.some(
      (p) => normalize(p.nome) === nomeNorm && normalize(p.cognome) === cognomeNorm,
    );
  }

  async createAndAssignProfilo(): Promise<void> {
    if (
      !this.isNewProfiloFormValid() ||
      this.savingNewProfilo ||
      !this.targetAssignmentForNewProfilo
    ) {
      return;
    }

    const nome = this.newProfiloNome.trim();
    const cognome = this.newProfiloCognome.trim();
    if (this.isProfiloDuplicate(nome, cognome)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Profilo già presente',
        detail: `Esiste già un profilo per ${nome} ${cognome}.`,
        life: 4000,
      });
      return;
    }

    this.savingNewProfilo = true;
    try {
      const created = await firstValueFrom(
        this.profiliService.createProfilo({ nome, cognome }),
      );
      const nuovoProfilo: ProfiloModel = {
        ...created,
        displayName: `${created.nome} ${created.cognome}`.trim(),
      };
      this.profili = [...this.profili, nuovoProfilo].sort((a, b) => {
        const byCognome = a.cognome.localeCompare(b.cognome, 'it', {
          sensitivity: 'base',
        });
        if (byCognome !== 0) return byCognome;
        return a.nome.localeCompare(b.nome, 'it', { sensitivity: 'base' });
      });
      this.targetAssignmentForNewProfilo.persona = nuovoProfilo;
      this.targetAssignmentForNewProfilo.profiliSuggestions = [nuovoProfilo];

      this.closeCreateProfiloDialog();
      this.messageService.add({
        severity: 'success',
        summary: 'Successo',
        detail: `${nuovoProfilo.displayName} creato e assegnato`,
        life: 3000,
      });
    } catch (err) {
      this.logger.error('[WIZARD] Errore nella creazione del profilo:', err);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile creare il profilo. Riprova.',
        life: 5000,
      });
    } finally {
      this.savingNewProfilo = false;
    }
  }

  /**
   * Ricarica i clienti dopo la creazione di un nuovo cliente.
   */
  private loadClienti(): void {
    this.requisitiService.getClienti().subscribe({
      next: (clienti) => {
        this.clienti = clienti;
        this.segnalaElencoCaricato('clienti');
      },
      error: (err) => {
        this.logger.error('[WIZARD] Errore nel caricamento dei clienti:', err);
        this.segnalaElencoNonCaricato('clienti');
      },
    });
  }

  /**
   * Ricarica le commesse dopo la creazione di una nuova commessa.
   */
  private loadCommesse(): void {
    this.requisitiService.getCommesse().subscribe({
      next: (commesse) => {
        this.commesse = commesse;
        this.segnalaElencoCaricato('commesse');
      },
      error: (err) => {
        this.logger.error(
          '[WIZARD] Errore nel caricamento delle commesse:',
          err,
        );
        this.segnalaElencoNonCaricato('commesse');
      },
    });
  }

  /**
   * Segnala nel banner che un elenco non è disponibile. Senza questo, una
   * ricarica fallita lascerebbe l'elenco fermo ai dati vecchi senza che
   * l'utente abbia modo di accorgersene.
   */
  private segnalaElencoNonCaricato(elenco: string): void {
    if (!this.datiWizardNonCaricati.includes(elenco)) {
      this.datiWizardNonCaricati = [...this.datiWizardNonCaricati, elenco];
    }
  }

  private segnalaElencoCaricato(elenco: string): void {
    this.datiWizardNonCaricati = this.datiWizardNonCaricati.filter(
      (e) => e !== elenco,
    );
  }

  /**
   * Autocompletamento per clienti
   */
  onClienteComplete(event: { query: string }): void {
    const query = (event.query || '').toLowerCase();
    this.clientiSuggestions = this.clienti.filter((cliente) =>
      cliente.ragioneSociale.toLowerCase().includes(query),
    );
  }

  /**
   * Autocompletamento per commesse - filtra in base al cliente selezionato
   */
  onCommessaComplete(event: { query: string }): void {
    const query = (event.query || '').toLowerCase();

    // Filtra le commesse in base al cliente selezionato
    let commesseFiltrate = this.commesse;
    if (this.selectedCliente) {
      commesseFiltrate = this.commesse.filter(
        (commessa) => commessa.clienteId === this.selectedCliente!.id,
      );
    }

    // Applica il filtro di ricerca
    this.commesseSuggestions = commesseFiltrate.filter(
      (commessa) =>
        commessa.codice.toLowerCase().includes(query) ||
        (commessa.descrizione &&
          commessa.descrizione.toLowerCase().includes(query)),
    );
  }

  /**
   * Gestisce la selezione di un cliente
   */
  onClienteSelect(event: any): void {
    const cliente: ClienteModel = event.value || event;
    this.selectedCliente = cliente;
    this.referenzaData.cliente = cliente.ragioneSociale;

    // Reset della commessa quando cambia il cliente
    this.selectedCommessa = null;
    this.codiceCommessaInput = '';
    this.referenzaData.codiceCommerciale = null;
    this.commesseSuggestions = [];

    this.logger.debug(
      '[WIZARD] Cliente selezionato:',
      cliente.ragioneSociale,
      'ID:',
      cliente.id,
    );
  }

  /**
   * Gestisce la deselezione del cliente
   */
  onClienteClear(): void {
    this.selectedCliente = null;
    this.referenzaData.cliente = null;

    // Reset della commessa quando viene deselezionato il cliente
    this.selectedCommessa = null;
    this.codiceCommessaInput = '';
    this.referenzaData.codiceCommerciale = null;
    this.commesseSuggestions = [];
  }

  /**
   * Formatta la visualizzazione della commessa selezionata
   */
  getCommessaDisplayValue(commessa: CommessaModel): string {
    if (!commessa) return '';
    if (commessa.descrizione) {
      return `${commessa.descrizione} (${commessa.codice})`;
    }
    return commessa.codice;
  }

  /**
   * Gestisce la selezione di una commessa dal dropdown
   */
  onCommessaSelect(event: any): void {
    this.logger.debug('[WIZARD] onCommessaSelect chiamato, event:', event);
    const commessa: CommessaModel = event.value || event;
    this.logger.debug('[WIZARD] Commessa selezionata:', commessa);

    // Verifica che commessa sia un oggetto valido con la proprietà codice
    if (!commessa || !commessa.codice) {
      this.logger.warn(
        '[WIZARD] Commessa non valida o senza codice:',
        commessa,
      );
      return;
    }

    // Salva l'intera commessa selezionata
    this.selectedCommessa = commessa;
    // Mantieni il codice come stringa per preservare gli zeri iniziali (es: "00000", "00021")
    this.referenzaData.codiceCommerciale = commessa.codice;

    // Formatta il display value per mostrare descrizione (codice)
    this.codiceCommessaInput = this.getCommessaDisplayValue(commessa);

    this.logger.debug(
      '[WIZARD] referenzaData.codiceCommerciale impostato a:',
      this.referenzaData.codiceCommerciale,
    );
    this.logger.debug('[WIZARD] Commessa selezionata:', this.selectedCommessa);
  }

  /**
   * Gestisce la selezione di una categoria servizio dall'autocomplete
   */
  onCategoriaServizioSelect(event: any): void {
    const categoria: string = (
      event.value ||
      event ||
      this.categoriaServizioInput ||
      ''
    )
      .toString()
      .trim()
      .toUpperCase();
    if (categoria && this.categorieServizioDisponibili.includes(categoria)) {
      // Imposta l'input e aggiungi la categoria
      this.categoriaServizioInput = categoria;
      this.addCategoriaServizio();
    }
  }

  /**
   * Gestisce il cambio manuale del codice commessa
   */
  onCodiceCommessaChange(): void {
    this.logger.debug(
      '[WIZARD] onCodiceCommessaChange chiamato, codiceCommessaInput:',
      this.codiceCommessaInput,
      'tipo:',
      typeof this.codiceCommessaInput,
    );

    // Se codiceCommessaInput è un oggetto (CommessaModel), estrai il codice
    let codiceStringa: string;
    if (
      typeof this.codiceCommessaInput === 'object' &&
      this.codiceCommessaInput !== null
    ) {
      this.logger.debug(
        '[WIZARD] codiceCommessaInput è un oggetto, estraggo il codice',
      );
      codiceStringa = (this.codiceCommessaInput as any).codice;
    } else {
      codiceStringa = this.codiceCommessaInput as string;
    }

    // Mantieni sempre come stringa per preservare gli zeri iniziali (es: "00000", "00021")
    if (codiceStringa && codiceStringa.trim() !== '') {
      this.referenzaData.codiceCommerciale = codiceStringa.trim();
      this.logger.debug(
        '[WIZARD] referenzaData.codiceCommerciale impostato a (stringa):',
        this.referenzaData.codiceCommerciale,
      );
    } else {
      this.referenzaData.codiceCommerciale = null;
      this.logger.debug('[WIZARD] codiceCommessaInput vuoto, impostato a null');
    }
  }

  /**
   * Carica i tag dal backend e li raggruppa per categoria
   */
  private loadTags(): void {
    this.tagService.getTags().subscribe({
      next: (tags) => {
        this.allTags = tags; // Salva tutti i tag in un array piatto
        const grouped: Record<string, TagModel[]> = {};
        for (const tag of tags) {
          if (!grouped[tag.categoriaTagCodice]) {
            grouped[tag.categoriaTagCodice] = [];
          }
          grouped[tag.categoriaTagCodice].push(tag);
        }
        this.tagsByCategory = grouped;
        Object.keys(grouped).forEach((cat) => {
          if (!this.selectedTagsByCategory[cat]) {
            this.selectedTagsByCategory[cat] = [];
          }
        });
        this.updateGroupedCategories();
        //this.logger.info('[WIZARD] Tag caricati, categorie:', Object.keys(grouped).length);
      },
      error: (err) => {
        this.logger.error('[WIZARD] Errore nel caricamento dei tag:', err);
      },
    });
  }

  /**
   * Aggiorna i gruppi di categorie quando cambiano i tag
   */
  private updateGroupedCategories(): void {
    // Nuovo modello: un modulo per ciascuna delle 5 categorie principali
    this.groupedCategories = this.tagCategoryOrder
      .map((code) => {
        const catData = this.tagsByCategory[code];
        if (!catData || catData.length === 0) {
          return null;
        }

        // Titolo: mostra il codice categoria, ma senza underscore per TIPO_STRUTTURE
        const label = code === 'TIPO_STRUTTURE' ? 'TIPO STRUTTURE' : code;

        return {
          groupName: code,
          groupLabel: label,
          groupIcon: this.tagCategoryIcons[code] || 'category',
          categories: [{ key: code, value: catData }],
        };
      })
      .filter(
        (
          group,
        ): group is {
          groupName: string;
          groupLabel: string;
          groupIcon: string;
          categories: Array<{ key: string; value: TagModel[] }>;
        } => group !== null,
      );
  }

  /**
   * Suggerimenti per l'autocomplete di una specifica categoria
   */
  /**
   * Ottiene le opzioni formattate per il dropdown multiselect di una categoria
   */
  getTagOptionsForCategory(
    catCode: string,
  ): Array<{ label: string; value: string }> {
    const tags = this.tagsByCategory[catCode] || [];
    return tags
      .slice()
      .sort((a, b) => {
        const la = a.codice.replace(/_/g, ' ');
        const lb = b.codice.replace(/_/g, ' ');
        return la.localeCompare(lb, 'it', { sensitivity: 'base' });
      })
      .map((t) => ({
        label: t.codice.replace(/_/g, ' '),
        value: t.codice,
      }));
  }

  /**
   * Restituisce le label dei tag selezionati per una categoria,
   * usate per mostrare i chip di fianco alla select nello step Tag.
   */
  getSelectedTagLabels(catCode: string): string[] {
    const selectedCodes = this.selectedTagsByCategory[catCode] || [];
    const tags = this.tagsByCategory[catCode] || [];

    return selectedCodes
      .map((code) => {
        const found = tags.find((t) => t.codice === code);
        const rawLabel = found ? found.codice : code;
        return rawLabel.replace(/_/g, ' ');
      })
      .filter((label) => !!label && label.trim().length > 0);
  }

  onCategoryComplete(cat: string, event: { query: string }): void {
    const all = this.tagsByCategory[cat] || [];
    const q = (event.query || '').toLowerCase();
    this.catSuggestions[cat] = all
      .map((t) => t.codice)
      .filter((c) => c.toLowerCase().includes(q));
  }

  /**
   * Aggiorna i tag selezionati e normalizza il formato
   */
  onTagsChanged(): void {
    const allTags: string[] = [];
    const allCategories: string[] = [];

    Object.keys(this.selectedTagsByCategory).forEach((cat) => {
      const tagCodes = this.selectedTagsByCategory[cat] || [];
      if (tagCodes.length > 0) {
        allCategories.push(cat);
        tagCodes.forEach((code) => {
          allTags.push(`${cat}#${code}`);
        });
      }
    });

    this.referenzaData.tag = allTags;
    this.referenzaData.categorieTag = allCategories;
  }

  /**
   * Converte i tag dal formato stringa (CATEGORIA#CODICE) al formato TagRiferimentoModel per l'API
   */
  private convertTagsToTagRiferimento(
    tagStrings: string[],
  ): TagRiferimentoModel[] {
    const tags: TagRiferimentoModel[] = [];

    tagStrings.forEach((tagString) => {
      const [categoriaCodice, tagCodice] = tagString.split('#');
      if (categoriaCodice && tagCodice) {
        tags.push({
          codiceCategoria: categoriaCodice,
          codiceTag: tagCodice,
        });
      }
    });

    return tags;
  }

  /**
   * Gestisce l'input delle categorie servizio - filtra solo i valori validi dalla lista
   */
  onCategoriaServizioInput(event: { query: string }): void {
    const query = (event.query || '').toUpperCase();
    // Filtra le categorie disponibili che non sono già state selezionate
    const categorieDisponibili = this.categorieServizioDisponibili.filter(
      (cat) => !this.referenzaData.categorieServizio?.includes(cat),
    );
    // Applica il filtro di ricerca
    this.categoriaServizioSuggestions = categorieDisponibili.filter((cat) =>
      cat.includes(query),
    );
  }

  /**
   * Aggiunge una categoria servizio - valida che sia nella lista e non duplicata
   */
  addCategoriaServizio(): void {
    const cat = this.categoriaServizioInput.trim().toUpperCase();

    // Verifica che la categoria non sia vuota
    if (!cat) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Inserisci una categoria servizio valida',
        life: 3000,
      });
      return;
    }

    // Verifica che la categoria sia nella lista consentita
    if (!this.categorieServizioDisponibili.includes(cat)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore Validazione',
        detail: `La categoria "${cat}" non è valida. Seleziona una categoria dalla lista.`,
        life: 4000,
      });
      return;
    }

    // Verifica che non ci siano duplicati
    if (this.referenzaData.categorieServizio?.includes(cat)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: `La categoria "${cat}" è già stata aggiunta`,
        life: 3000,
      });
      return;
    }

    // Aggiungi la categoria
    if (!this.referenzaData.categorieServizio) {
      this.referenzaData.categorieServizio = [];
    }
    this.referenzaData.categorieServizio.push(cat);
    this.categoriaServizioInput = '';
    this.categoriaServizioSuggestions = [];
  }

   /**
   * Rimuove una categoria servizio
   */
  removeCategoriaServizio(cat: string): void {
    if (this.referenzaData.categorieServizio) {
      this.referenzaData.categorieServizio =
        this.referenzaData.categorieServizio.filter((c) => c !== cat);
    }
  }

  /**
   * Quando i servizi accessori vengono disattivati, azzera l'URL
   */
  onServiziAccessoriToggle(event: { checked: boolean }): void {
    if (!event.checked) {
      this.referenzaData.urlServiziAccessori = null;
    }
  }

  /**
   * Sincronizza i servizi accessori tramite PATCH dedicato
   */
  private async syncServiziAccessori(referenzaId: number): Promise<void> {
    const presenti = this.referenzaData.serviziAccessoriPresenti === true;
    if (presenti) {
      const url = this.referenzaData.urlServiziAccessori?.trim() || null;
      await firstValueFrom(
        this.apiService.updateServiziAccessori(referenzaId, {
          serviziAccessoriPresenti: true,
          urlServiziAccessori: url,
        }),
      );
    } else {
      await firstValueFrom(
        this.apiService.updateServiziAccessori(referenzaId, {
          serviziAccessoriPresenti: false,
        }),
      );
    }
  }

  /**
   * Aggiunge un codice certificato
   */
  addCodiceCertificato(): void {
    const codice = this.codiceCertificatoInput.trim();

    // Verifica che il codice non sia vuoto
    if (!codice) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Inserisci un codice certificato valido',
        life: 3000,
      });
      return;
    }

    // Verifica che non ci siano duplicati
    if (this.referenzaData.codiciCertificati?.includes(codice)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: `Il codice certificato "${codice}" è già stato aggiunto`,
        life: 3000,
      });
      return;
    }

    // Aggiungi il codice
    if (!this.referenzaData.codiciCertificati) {
      this.referenzaData.codiciCertificati = [];
    }
    this.referenzaData.codiciCertificati.push(codice);
    this.codiceCertificatoInput = '';

    this.logger.debug('[WIZARD] Codice certificato aggiunto:', codice);
  }

  /**
   * Rimuove un codice certificato
   */
  removeCodiceCertificato(codice: string): void {
    if (this.referenzaData.codiciCertificati) {
      this.referenzaData.codiciCertificati =
        this.referenzaData.codiciCertificati.filter((c) => c !== codice);
      this.logger.debug('[WIZARD] Codice certificato rimosso:', codice);
    }
  }

  /**
   * Valida e filtra le categorie servizio per rimuovere quelle non valide e i duplicati
   */
  private validateCategorieServizio(categorie: string[]): string[] {
    if (!categorie || categorie.length === 0) {
      return [];
    }
    // Converte tutte le categorie in maiuscolo e filtra solo quelle valide e uniche
    const categorieValide: string[] = [];
    const categorieViste = new Set<string>();

    for (const cat of categorie) {
      const catUpper = cat.trim().toUpperCase();
      // Verifica che sia nella lista consentita e non duplicata
      if (
        this.categorieServizioDisponibili.includes(catUpper) &&
        !categorieViste.has(catUpper)
      ) {
        categorieValide.push(catUpper);
        categorieViste.add(catUpper);
      }
    }

    return categorieValide;
  }

  // Navigation methods
  nextStep(): void {
    if (this.canProceedToNext()) {
      const realStep = this.getRealStepIndex(this.currentStep);
      const nextRealStep = this.getNextRealStep(realStep);
      if (nextRealStep !== null) {
        this.currentStep = this.getDisplayStepIndex(nextRealStep);
      }
    }
  }

  prevStep(): void {
    if (this.currentStep > 0) {
      const realStep = this.getRealStepIndex(this.currentStep);
      const prevRealStep = this.getPrevRealStep(realStep);
      if (prevRealStep !== null) {
        this.currentStep = this.getDisplayStepIndex(prevRealStep);
      }
    }
  }

  goToStep(stepIndex: number): void {
    const realStep = this.getRealStepIndex(stepIndex);
    if (realStep >= 0 && realStep < this.steps.length) {
      if (this.getSkippedStepIndexes().includes(realStep)) {
        return;
      }
      this.currentStep = stepIndex;
    }
  }

  canProceedToNext(): boolean {
    // Primo step: richiede almeno il titolo breve
    if (this.currentStep === 0) {
      return !!this.referenzaData.titoloBreve?.trim();
    }

    // Ultimo step (riepilogo): il bottone Avanti non è visibile, ma per sicurezza ritorniamo false
    const isLastVisibleStep = this.currentStep >= this.visibleSteps.length - 1;
    if (isLastVisibleStep) {
      return false;
    }

    // Tutti gli altri step (Dettagli, Tag, Ruoli e soggetti) sono opzionali
    return true;
  }

  /**
   * Formatta una data per il formato ISO richiesto da Firestore
   */
  formatDateForFirestore(date: Date | null | undefined): string | null {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Salva la referenza chiamando l'API POST del backend
   */
  async saveReferenza(): Promise<void> {
    this.logger.debug('[WIZARD] saveReferenza chiamato - Stato corrente:');
    this.logger.debug(
      '[WIZARD] - codiceCommessaInput:',
      this.codiceCommessaInput,
    );
    this.logger.debug(
      '[WIZARD] - referenzaData.codiceCommerciale:',
      this.referenzaData.codiceCommerciale,
    );
    this.logger.debug('[WIZARD] - selectedCliente:', this.selectedCliente);

    if (!this.referenzaData.titoloBreve?.trim()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore Validazione',
        detail: 'Il titolo breve è obbligatorio',
        life: 5000,
      });
      return;
    }

    this.saving = true;

    try {
      // Valida quotaCooprogetti (max 100%)
      if (
        this.referenzaData.quotaCooprogetti !== null &&
        this.referenzaData.quotaCooprogetti !== undefined
      ) {
        if (this.referenzaData.quotaCooprogetti > 100) {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore Validazione',
            detail: 'La quota Cooprogetti non può essere superiore al 100%',
            life: 5000,
          });
          this.saving = false;
          return;
        }
      }

      // Converte i tag da formato stringa (CATEGORIA#CODICE) al formato TagRiferimentoModel
      // Se si sta modificando, usa i tag originali della referenza senza modifiche
      let tagsToConvert: string[] = [];
      if (this.referenzaToEdit) {
        // Quando si modifica, usa i tag originali così come recuperati
        tagsToConvert = this.referenzaToEdit.tag || [];
      } else {
        // Quando si crea una nuova referenza, usa i tag dal form
        tagsToConvert = this.referenzaData.tag || [];
      }
      const tags = this.convertTagsToTagRiferimento(tagsToConvert);

      // Converte quotaCooprogetti da percentuale a decimale (100% = 1.0, 50% = 0.5)
      let quotaCooprogettiDecimale: number | null = null;
      if (
        this.referenzaData.quotaCooprogetti !== null &&
        this.referenzaData.quotaCooprogetti !== undefined
      ) {
        quotaCooprogettiDecimale = this.referenzaData.quotaCooprogetti / 100;
      }

      // Prepara il DTO per l'API (senza id, dataCreazione e dataModifica)
      const referenzaDto = {
        codiceCommessa:
          this.referenzaData.codiceCommerciale !== null &&
          this.referenzaData.codiceCommerciale !== undefined
            ? (this.referenzaData.codiceCommerciale as string)
            : null,
        codiciCertificati:
          this.referenzaData.codiciCertificati &&
          this.referenzaData.codiciCertificati.length > 0
            ? this.referenzaData.codiciCertificati
            : null,
        projectManager: this.referenzaData.projectManager || null,
        cliente: this.referenzaData.cliente || null,
        titoloBreve: this.referenzaData.titoloBreve,
        titoloEsteso: this.referenzaData.titoloEsteso || null,
        tempistiche: this.referenzaData.tempistiche || null,
        dataFine:
          this.formatDateForFirestore(
            (this.referenzaData.dataFine as Date) || null,
          ) || null,
        rtp: this.referenzaData.rtp || false,
        capogruppo: this.referenzaData.capogruppo || false,
        quotaCooprogetti: quotaCooprogettiDecimale,
        inAq: this.referenzaData.inAq || false,
        bim: this.referenzaData.bim || false,
        valoreDimensione: this.referenzaData.valoreDimensione || null,
        importoLavori:
          this.referenzaData.importoLavori !== null &&
          this.referenzaData.importoLavori !== undefined
            ? this.referenzaData.importoLavori
            : null,
        importoServizio:
          this.referenzaData.importoServizio !== null &&
          this.referenzaData.importoServizio !== undefined
            ? this.referenzaData.importoServizio
            : null,
        categorieServizio: this.referenzaData.categorieServizio || null,
        tag: tags.length > 0 ? tags : null,
        latitudine: this.posizioneLatitude ?? null,
        longitudine: this.posizioneLongitudine ?? null,
      };

      this.logger.debug("[WIZARD] Invio DTO referenza all'API:", referenzaDto);
      this.logger.debug(
        '[WIZARD] Dettagli campi chiave - codiceCommerciale:',
        this.referenzaData.codiceCommerciale,
        'codiceCertificato:',
        this.referenzaData.codiceCertificato,
      );

      // Se c'è una referenza da modificare, chiama PUT, altrimenti POST
      if (this.referenzaToEdit) {
        // Per la modifica, dobbiamo includere tutti i campi incluso id
        const referenzaUpdateDto: ReferenzaModel = {
          id: this.referenzaToEdit.id,
          ...referenzaDto,
        } as ReferenzaModel;

        await firstValueFrom(
          this.apiService.updateReferenza(
            this.referenzaToEdit.id,
            referenzaUpdateDto,
          ),
        );

        try {
          await this.syncServiziAccessori(this.referenzaToEdit.id);
        } catch (saError) {
          this.logger.error(
            '[WIZARD] Errore nel salvataggio dei servizi accessori:',
            saError,
          );
          this.messageService.add({
            severity: 'warn',
            summary: 'Attenzione',
            detail:
              'Referenza modificata, ma i servizi affini potrebbero non essere stati salvati.',
            life: 7000,
          });
        }

        // In modifica i ruoli non sono modificabili: nessuna sincronizzazione

        this.logger.info(
          '[WIZARD] Referenza modificata con successo tramite API',
        );

        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Referenza modificata con successo',
          life: 3000,
        });
      } else {
        // Chiama l'API POST per creare la referenza
        const created = await firstValueFrom(
          this.apiService.createReferenza(referenzaDto),
        );

        this.logger.info(
          '[WIZARD] Referenza creata con successo tramite API, id:',
          created.id,
        );

        // Se abbiamo ruoli assegnati, crea anche le associazioni ruolo/profilo
        const newId = created.id;
        if (newId && this.ruoliAssignments.length > 0) {
          const assignmentsToSend = this.ruoliAssignments.filter(
            (a) => a.persona,
          );

          try {
            await Promise.all(
              assignmentsToSend.map((a) => {
                if (this.isProfiloModel(a.persona)) {
                  return firstValueFrom(
                    this.apiService.assignProfiloToRuolo(
                      newId,
                      a.ruolo.id,
                      a.persona.id,
                      { firmatario: a.firmatario },
                    ),
                  );
                }

                const manualProfilo = this.parseManualProfilo(a.persona);
                if (!manualProfilo) {
                  return Promise.resolve();
                }

                return firstValueFrom(
                  this.apiService.assignManualProfiloToRuolo(
                    newId,
                    a.ruolo.id,
                    {
                      nome: manualProfilo.nome,
                      cognome: manualProfilo.cognome,
                      firmatario: a.firmatario,
                    },
                  ),
                );
              }),
            );
            this.logger.info(
              '[WIZARD] Assegnazioni ruolo/profilo create con successo:',
              assignmentsToSend.length,
            );
          } catch (assignError) {
            this.logger.error(
              "[WIZARD] Errore durante l'assegnazione dei profili ai ruoli:",
              assignError,
            );
            this.messageService.add({
              severity: 'warn',
              summary: 'Attenzione',
              detail:
                'Referenza creata, ma alcune assegnazioni di ruoli potrebbero non essere state salvate. Verifica in configurazione.',
              life: 7000,
            });
          }
        }

        if (newId && this.referenzaData.serviziAccessoriPresenti === true) {
          try {
            await this.syncServiziAccessori(newId);
          } catch (saError) {
            this.logger.error(
              '[WIZARD] Errore nel salvataggio dei servizi accessori:',
              saError,
            );
            this.messageService.add({
              severity: 'warn',
              summary: 'Attenzione',
              detail:
                'Referenza creata, ma i servizi affini potrebbero non essere stati salvati.',
              life: 7000,
            });
          }
        }

        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Referenza creata con successo',
          life: 3000,
        });
      }

      // Chiudi il wizard e emetti l'evento
      this.closeWizard();
      this.referenzaCreated.emit();
    } catch (error) {
      this.logger.error(
        '[WIZARD] Errore nel salvataggio della referenza:',
        error,
      );
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile salvare la referenza. Riprova più tardi.',
        life: 5000,
      });
    } finally {
      this.saving = false;
    }
  }

  /**
   * Chiude il wizard
   */
  /**
   * Carica i dati di una referenza esistente nel form per la modifica
   */
  private loadReferenzaData(ref: ReferenzaFirestore): void {
    // Gestisce la retrocompatibilità: se c'è il vecchio codiceCertificato e non ci sono codiciCertificati,
    // migra il vecchio certificato nell'array
    let certificati: string[] = [];
    if (
      (ref as any).codiciCertificati &&
      (ref as any).codiciCertificati.length > 0
    ) {
      certificati = [...(ref as any).codiciCertificati];
    }
    // Aggiungi il vecchio certificato singolo se presente e non già incluso
    if (ref.codiceCertificato) {
      const certString = ref.codiceCertificato.toString();
      if (!certificati.includes(certString)) {
        certificati.push(certString);
      }
    }

    this.referenzaData = {
      codiceCommerciale:
        ref.codiceCommerciale !== null && ref.codiceCommerciale !== undefined
          ? ref.codiceCommerciale
          : null,
      codiciCertificati: certificati,
      projectManager: ref.projectManager || null,
      cliente: ref.cliente || null,
      titoloBreve: ref.titoloBreve || '',
      titoloEsteso: ref.titoloEsteso || null,
      tempistiche: ref.tempistiche || null,
      dataFine: ref.dataFine ? new Date(ref.dataFine) : null,
      rtp: ref.rtp || false,
      capogruppo: ref.capogruppo || false,
      quotaCooprogetti:
        ref.quotaCooprogetti !== null && ref.quotaCooprogetti !== undefined
          ? ref.quotaCooprogetti * 100
          : null, // Converti da decimale a percentuale
      inAq: ref.inAq || false,
      bim: ref.bim || false,
      valoreDimensione: ref.valoreDimensione || null,
      importoLavori:
        ref.importoLavori !== null && ref.importoLavori !== undefined
          ? ref.importoLavori
          : null,
      importoServizio:
        ref.importoServizio !== null && ref.importoServizio !== undefined
          ? ref.importoServizio
          : null,
      categorieServizio: this.validateCategorieServizio(
        ref.categorieServizio || [],
      ),
      tag: ref.tag || [],
      categorieTag: ref.categorieTag || [],
      serviziAccessoriPresenti: ref.serviziAccessoriPresenti === true,
      urlServiziAccessori: ref.urlServiziAccessori || null,
    };

    // Georeferenziazione: Firestore GeoPoint -> step posizione
    const pos = ref.posizione ?? null;
    if (pos) {
      this.geoSelectedPosition = { lat: pos.latitude, lng: pos.longitude };
      this.geoMapCenter = this.geoSelectedPosition;
      this.geoMapZoom = 16;
    } else {
      this.clearGeoPosizione();
    }

    // Imposta il cliente selezionato per l'autocomplete
    if (ref.cliente) {
      const cliente = this.clienti.find(
        (c) => c.ragioneSociale === ref.cliente,
      );
      if (cliente) {
        this.selectedCliente = cliente;
        // Gestisce sia string che number (per retrocompatibilità con dati vecchi)
        this.codiceCommessaInput =
          ref.codiceCommerciale !== null && ref.codiceCommerciale !== undefined
            ? typeof ref.codiceCommerciale === 'string'
              ? ref.codiceCommerciale
              : ref.codiceCommerciale.toString()
            : '';
      }
    }

    this.logger.debug(
      '[WIZARD] Dati referenza caricati per modifica:',
      this.referenzaData,
    );

    // Ricostruisce ruoliAssignments dai dati ruoliProfili già presenti in Firestore
    this.originalRuoliProfili = [...(ref.ruoliProfili ?? [])];
    this.ruoliAssignments = [];
    if (this.originalRuoliProfili.length > 0) {
      this.ruoliAssignments = this.originalRuoliProfili
        .map((rp) => {
          const ruolo = this.ruoliDisponibili.find(
            (r) => r.codice === rp.ruoloCodice,
          );
          if (!ruolo) return null;
          const persona: ProfiloModel | null =
            this.profili.find((p) => p.id === rp.profiloId) ?? null;
          return {
            ruolo,
            persona: persona ?? rp.nomeCompleto ?? null,
            firmatario: rp.firmatario,
          } as RuoloAssignment;
        })
        .filter((a): a is RuoloAssignment => a !== null);
    }

    this.logger.debug(
      '[WIZARD] Ruoli esistenti caricati per modifica:',
      this.ruoliAssignments.length,
    );
  }

  /**
   * Reset del form per una nuova referenza
   */
  private resetForm(): void {
    this.referenzaData = {
      codiceCommerciale: null,
      codiceCertificato: null,
      codiciCertificati: [],
      projectManager: null,
      cliente: null,
      titoloBreve: '',
      titoloEsteso: null,
      tempistiche: null,
      dataFine: null,
      rtp: false,
      capogruppo: false,
      quotaCooprogetti: null,
      inAq: false,
      bim: false,
      valoreDimensione: null,
      importoLavori: null,
      importoServizio: null,
      categorieServizio: [],
      tag: [],
      categorieTag: [],
      serviziAccessoriPresenti: false,
      urlServiziAccessori: null,
    };
    this.geoAddressInput = '';
    this.geoCityInput = '';
    this.geoSearching = false;
    this.lastGeocodeConfidence = null;
    this.clearGeoPosizione();
    this.selectedCliente = null;
    this.selectedCommessa = null;
    this.codiceCommessaInput = '';
    this.ruoliAssignments = [];
    this.ruoliSearchText = '';
    this.originalRuoliProfili = [];
    this.closeCreateProfiloDialog();
    this.currentStep = 0;
  }

  closeWizard(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetWizard();
    this.wizardClosed.emit();
  }

  /**
   * Reset del wizard
   */
  resetWizard(): void {
    this.currentStep = 0;
    this.referenzaData = {
      codiceCommerciale: null,
      codiceCertificato: null,
      codiciCertificati: [],
      projectManager: null,
      cliente: null,
      titoloBreve: '',
      titoloEsteso: null,
      tempistiche: null,
      dataFine: null,
      rtp: false,
      capogruppo: false,
      quotaCooprogetti: null,
      inAq: false,
      valoreDimensione: null,
      importoLavori: null,
      importoServizio: null,
      categorieServizio: [],
      tag: [],
      categorieTag: [],
      serviziAccessoriPresenti: false,
      urlServiziAccessori: null,
    };
    this.geoAddressInput = '';
    this.geoCityInput = '';
    this.geoSearching = false;
    this.lastGeocodeConfidence = null;
    this.clearGeoPosizione();
    this.selectedTagsByCategory = {};
    Object.keys(this.tagsByCategory).forEach((cat) => {
      this.selectedTagsByCategory[cat] = [];
    });
    this.categoriaServizioInput = '';
    this.codiceCommessaInput = '';
    this.selectedCliente = null;
    this.selectedCommessa = null;
    this.clientiSuggestions = [];
    this.commesseSuggestions = [];
    this.commesse = [];
    this.ruoliAssignments = [];
    this.ruoliSearchText = '';
    this.originalRuoliProfili = [];
    this.closeCreateProfiloDialog();
  }

  /**
   * Formatta una data per la visualizzazione
   */
  formatDate(date: Date | string | null | undefined): string {
    if (!date) return 'N/D';
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return 'N/D';
    return d.toLocaleDateString('it-IT');
  }

  /**
   * Formatta un importo per la visualizzazione
   */
  formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined) return 'N/D';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  }

  /**
   * Formatta la quota cooprogetti per la visualizzazione (es: 50,25%)
   */
  formatQuotaCooprogetti(value: number | null | undefined): string {
    if (value === null || value === undefined) return 'N/D';
    // Mostra decimali solo se diversi da 0
    const hasDecimals = value % 1 !== 0;
    return (
      new Intl.NumberFormat('it-IT', {
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: 2,
      }).format(value) + '%'
    );
  }

  /**
   * Verifica se l'utente può modificare (non è VIEWER)
   */
  canModify(): boolean {
    return this.userRoleService.canModify();
  }

  /**
   * Gestisce la creazione di un nuovo tag da un dropdown
   * Identico al comportamento nella home
   */
  onCreateNewTagFromDropdown(categoryCode: string): void {
    if (!this.canModify()) {
      return;
    }
    this.createTagInitialCategory = categoryCode;
    this.showCreateTagDialog = true;
  }

  /**
   * Gestisce la creazione di un tag nel wizard
   * Ricarica i tag per aggiornare i dropdown
   */
  onTagCreatedInWizard(): void {
    // Ricarica tags per aggiornare i dropdown e le categorie raggruppate
    this.loadTags();
  }

  /**
   * Gestisce la creazione di un nuovo cliente dal dropdown
   */
  onCreateNewClienteFromDropdown(): void {
    if (!this.canModify()) {
      return;
    }
    this.showCreateClienteDialog = true;
  }

  /**
   * Gestisce la creazione di un cliente nel wizard
   * Ricarica i clienti per aggiornare il dropdown
   */
  onClienteCreatedInWizard(): void {
    // Ricarica clienti per aggiornare il dropdown
    this.loadClienti();
  }

  /**
   * Gestisce la creazione di una nuova commessa dal dropdown
   */
  onCreateNewCommessaFromDropdown(): void {
    if (!this.canModify() || !this.selectedCliente) {
      return;
    }
    this.showCreateCommessaDialog = true;
  }

  /**
   * Gestisce la creazione di una commessa nel wizard
   * Ricarica le commesse per aggiornare il dropdown
   */
  onCommessaCreatedInWizard(): void {
    // Ricarica commesse per aggiornare il dropdown
    this.loadCommesse();
  }

  async searchGeoPosizione(): Promise<void> {
    if (!this.canModify()) return;

    const address = (this.geoAddressInput || '').trim();
    const city = (this.geoCityInput || '').trim();
    if (!address && !city) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Inserisci almeno la città oppure un indirizzo.',
        life: 3500,
      });
      return;
    }

    this.geoSearching = true;
    this.lastGeocodeConfidence = null;
    try {
      const fullQuery = [address, city].filter(Boolean).join(', ');
      let results = await firstValueFrom(
        this.geocodingService.search(fullQuery, 5),
      );
      if ((!results || results.length === 0) && city) {
        results = await firstValueFrom(this.geocodingService.search(city, 5));
      }
      if (!results || results.length === 0) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Nessun risultato',
          detail:
            'Non sono state trovate coordinate. Prova a specificare meglio indirizzo e città.',
          life: 4500,
        });
        return;
      }

      const best = results.find((r) => r.confidence === 'exact') ?? results[0];
      this.geoSelectedPosition = { lat: best.lat, lng: best.lng };
      this.geoMapCenter = this.geoSelectedPosition;
      this.geoMapZoom = best.confidence === 'exact' ? 16 : 13;
      this.lastGeocodeConfidence = best.confidence;
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail:
          'Impossibile contattare il servizio di geocoding. Riprova più tardi.',
        life: 5000,
      });
    } finally {
      this.geoSearching = false;
    }
  }

  /**
   * Calcola un'altezza "responsive" per la mappa nello step Posizione
   * in base all'altezza della finestra, così da sfruttare quasi tutto
   * lo spazio verticale disponibile nel dialog.
   */
  private computeGeoMapHeight(): void {
    if (typeof window === 'undefined') {
      this.geoMapHeightPx = 420;
      return;
    }

    const vh = window.innerHeight || 0;
    // Circa il 60% della viewport, con limiti di sicurezza
    const target = Math.max(340, Math.min(780, Math.round(vh * 0.6)));
    this.geoMapHeightPx = target;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.computeGeoMapHeight();
  }
}
